import React, { useState, useEffect } from "react"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import { XMarkIcon } from "@heroicons/react/24/outline"
import CMSRender from "aldoo-ra/CMS/cms-render"
import Value from "aldoo-ra/Value"

// Helper function defined outside component
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

// Animation states and constants defined outside the component
const ANIMATION_STATES = {
  HIDDEN: "hidden",
  VISIBLE: "visible",
  EXITING: "exiting",
}

const ANIMATION_STYLES = {
  [ANIMATION_STATES.HIDDEN]: {
    transform: "translateY(500px)",
    opacity: 0,
    transition: "none",
  },
  [ANIMATION_STATES.VISIBLE]: {
    transform: "translateY(0)",
    opacity: 1,
    transition:
      "transform 700ms cubic-bezier(0.16, 1, 0.3, 1), opacity 700ms ease-out",
  },
  [ANIMATION_STATES.EXITING]: {
    transform: "translateY(500px)",
    opacity: 0,
    transition: "transform 700ms ease-in-out, opacity 700ms ease-in-out",
  },
}

// Animation timing constants
const INITIAL_DELAY = 1500
const EXIT_DURATION = 700

const CookieBanner = () => {
  const [animState, setAnimState] = useState(ANIMATION_STATES.HIDDEN)
  const [isVisible, setIsVisible] = useState(false)
  const [cookieAccepted, setCookieAccepted] = Value(
    "cookie-accepted",
    false,
    "cookie-accepted"
  )

  // Effect for handling the initial appearance
  useEffect(() => {
    if (cookieAccepted) return

    let isMounted = true

    const showBanner = async () => {
      try {
        // First make the banner visible but in hidden state
        setIsVisible(true)

        // Wait for the initial delay
        await wait(INITIAL_DELAY)
        if (!isMounted) return

        // Animate to visible state
        setAnimState(ANIMATION_STATES.VISIBLE)
      } catch (error) {
        console.error("Animation error:", error)
      }
    }

    showBanner()

    return () => {
      isMounted = false
    }
  }, [cookieAccepted])

  const handleClose = async () => {
    try {
      // Animate to exiting state (with transition)
      setAnimState(ANIMATION_STATES.EXITING)

      // Wait for exit animation to complete
      await wait(EXIT_DURATION)

      // Remove from DOM
      setIsVisible(false)
    } catch (error) {
      console.error("Close animation error:", error)
      setIsVisible(false)
    }
  }

  const handleAccept = async () => {
    try {
      // Set cookie acceptance
      setCookieAccepted(true)
      // Animate to exiting state (with transition)
      setAnimState(ANIMATION_STATES.EXITING)

      // Wait for exit animation to complete
      await wait(EXIT_DURATION)

      // Remove from DOM
      setIsVisible(false)
    } catch (error) {
      console.error("Accept animation error:", error)
      setIsVisible(false)
    }
  }

  // Don't render anything if not visible or cookies accepted
  if (!isVisible || cookieAccepted) {
    return null
  }

  return (
    <CMSRender
      contentType="CookieBanner"
      renderItem={(data) => {
        return (
          <div className="fixed left-0 right-0 bottom-0 flex justify-center z-50 px-4 pointer-events-none">
            <div
              className="
                relative bg-gray_100 rounded-[30px] w-[343px] md:w-auto md:rounded-full
                shadow-lg flex flex-col md:flex-row items-center justify-between gap-[30px]
                md:gap-6 px-7 py-5 md:py-[10px] mb-9 pointer-events-auto
                will-change-transform
              "
              style={{
                ...ANIMATION_STYLES[animState],
                willChange: "transform, opacity",
              }}
            >
              <Typography
                variant="body1"
                className="text-text text-center text-base my-0"
              >
                {data.info}{" "}
                {data.learn_more?.[0]?.link?.[0]?.url && (
                  <span>
                    <a
                      href={data.learn_more[0].link[0].url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-bold underline"
                    >
                      {data.learn_more[0].label}
                    </a>
                  </span>
                )}
              </Typography>
              <Button
                onClick={handleAccept}
                variant="primary_s"
                className="min-w-[122px]"
              >
                {data.cta?.[0]?.label}
              </Button>
              <button
                onClick={handleClose}
                className="absolute right-0 -top-2 w-6 h-6 flex items-center justify-center text-gray-500 hover:text-gray-700 bg-gray_200 rounded-full"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
        )
      }}
    />
  )
}

export default CookieBanner
